import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, query, orderBy } from "firebase/firestore";
import "./Modals.css"
import "./Flashcards.css";
import { updateSM2 } from "./sm2"; // adjust path if needed
import FlashcardRating from "./FlashcardRating.jsx";
import { doc, updateDoc } from "firebase/firestore";
import { Timestamp, where } from "firebase/firestore";

const Flashcards = ({ selectedClassId, selectedUnitId, selectedUnitName, userId }) => {
  const [showOverlay, setShowOverlay] = useState(false);
  const [showAnswer, setShowAnswer] = useState(false);
  const [flashcards, setFlashcards] = useState([]);
  const [currentCard, setCurrentCard] = useState(null);
  const [masteryPercent, setMasteryPercent] = useState(0);
  const [allCards, setAllCards] = useState([]);

  const calculateMasteryPercent = (cards) => {
    if (!cards || cards.length === 0) return 0;
    const mastered = cards.filter(
      (c) => (c.repetitions ?? 0) >= 3 && (c.EF ?? 0) >= 2.5
    ).length;
    return Math.round((mastered / cards.length) * 100);
  };

  // lock page scroll while overlay is open
  useEffect(() => {
    document.body.style.overflow = showOverlay ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [showOverlay]);

  // spacebar reveal
  useEffect(() => {
    if (!showOverlay) return;
    const handleKey = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setShowAnswer(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [showOverlay]);

  // fetch flashcards from Firestore
  useEffect(() => {
    const fetchFlashcards = async () => {
      if (!userId || !selectedClassId || !selectedUnitId) {
        console.warn("Missing props:", { userId, selectedClassId, selectedUnitId });
        return;
      }

      try {
        const db = getFirestore();
        const flashcardsRef = collection(
          db,
          "Users",
          userId,
          "Classes",
          selectedClassId,
          "Units",
          selectedUnitId,
          "Flashcards"
        );

        console.log("✅ Querying path:", `Users/${userId}/Classes/${selectedClassId}/Units/${selectedUnitId}/Flashcards`);

        const q = query(
          flashcardsRef,
          where("dueDate", "<=", Timestamp.now()),
          orderBy("createdAt")
        );

        let snapshot = await getDocs(q);

        // 2. If no due cards, fetch all cards as fallback
        if (snapshot.empty) {
          console.log("🕓 No cards due — loading all flashcards.");
          snapshot = await getDocs(query(flashcardsRef, orderBy("createdAt")));
        } else {
          console.log("✅ Due flashcards found:", snapshot.size);
        }

        const data = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setAllCards(data);
        setMasteryPercent(calculateMasteryPercent(data));
        setFlashcards(data);
        setCurrentCard(data.length > 0 ? data[0] : null);
      } catch (err) {
        console.error("🔥 Error fetching flashcards:", err);
      }
    };

    fetchFlashcards();
  }, [userId, selectedClassId, selectedUnitId]);

  const handleRating = async (quality) => {
    if (!currentCard || !userId || !selectedClassId || !selectedUnitId) return;

    const updatedFields = updateSM2(currentCard, quality);
    const db = getFirestore();
    const cardRef = doc(
      db,
      "Users",
      userId,
      "Classes",
      selectedClassId,
      "Units",
      selectedUnitId,
      "Flashcards",
      currentCard.id
    );

    try {
      // ✅ 1. Update the current card in Firestore
      await updateDoc(cardRef, updatedFields);
      console.log("✅ Updated flashcard:", currentCard.id, updatedFields);

      // ✅ 2. Update allCards with this card's new fields
      const updatedCard = { ...currentCard, ...updatedFields };
      const newAllCards = allCards.map(c =>
        c.id === currentCard.id ? updatedCard : c
      );
      setAllCards(newAllCards);

      // ✅ 3. Recalculate mastery %
      setMasteryPercent(calculateMasteryPercent(newAllCards));

      // ✅ 4. Remove current card from the session array
      const remaining = flashcards.filter((card) => card.id !== currentCard.id);
      setFlashcards(remaining);

      // ✅ 5. Set the next card (or null if none left)
      setCurrentCard(remaining[0] || null);
      setShowAnswer(false);
    } catch (err) {
      console.error("❌ Failed to update flashcard:", err);
    }
  };

  return (
    <>
      <div
        className="section-card"
        onClick={() => {
          if (!selectedClassId || !selectedUnitId) {
            alert("Please select a class and unit first.");
            return;
          }
          setShowOverlay(true);
          setShowAnswer(false);
        }}
      >
        <h2>Flashcards</h2>
        <p>
          Flashcards for {selectedUnitName || "no unit selected"}
        </p>

      </div>

      {showOverlay && (
        <div
          className="flashcard-fullscreen"
          onClick={() => setShowAnswer(true)}
          role="button"
          aria-label="Close flashcards overlay"
        >
          <button
            className="flashcard-close"
            onClick={(e) => {
              e.stopPropagation();
              setShowOverlay(false);
            }}
          >
            ×
          </button>

          <div 
            className="flashcard-header" 
            style={{ marginTop: "30px" }}
          >
            {currentCard.question}
          </div>


          {!currentCard ? null : showAnswer ? (
            <p className="flashcard-answer">{currentCard.answer}</p>
          ) : null}


          {showAnswer && currentCard && (
            <FlashcardRating onRate={handleRating} />
          )}
        </div>
      )}
    </>
  );
};

export default Flashcards;
